import {
  Dispatch,
  FC,
  SetStateAction,
  memo,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import MessagesHistory from 'src/components/ChatAgent/MessagesHistory'
import GoalsPopupCanvas from './GoalsPopupCanvas'
import { Icon } from '@iconify/react'
import { AuthContext } from 'src/components/Auth/AuthProvider'
import { ChatInput } from 'src/components/inputs/ChatInput'
import TTSPlayer from 'src/components/TTSPlayer'
import { deleteLocalStorageGoalSession, getOrCreateGoalSession } from './utils'
import useGetChatMessages from 'src/apis/useGetChatMessages'
import { NotificationContext } from 'src/context/NotificationContext'
import SubmitButton from './SubmitButton'
import { CAIIconAnim } from 'src/components/CaiIcon/CAIIconAnim'
import { OrganizationCategory } from 'src/gql/graphql'
import Tooltip from 'src/components/UI/Tooltip'
import { queryClient } from 'src/libs/react-query.lib'
import QUERY_KEYS from 'src/constants/queryKey'
import { CaiPopupContext } from 'src/context/CaiPopupContext'

interface GoalsPopupProps {
  flow?: string
  setFlow: Dispatch<SetStateAction<string>>
  awaitingResponse: boolean
  onMessage: (msg: string, hidden: boolean) => void
  onChangeMessageText: (str: string) => void
  setAwaitingResponse: Dispatch<SetStateAction<boolean>>
  txtMessage: string
  setTxtMessage: Dispatch<SetStateAction<string>>
  messages: Message[]
  newChat: (selectedFlow?: string) => Promise<void>
  setMessagesMap: Dispatch<SetStateAction<Record<string, Message[]>>>
  canvasResponse?: string
  setCanvasResponse: Dispatch<SetStateAction<Record<string, Message[]>>>
}

export type Message = {
  message: string
  type: 'received' | 'sent'
  owner: string
  time: string
  images: string[]
}

const GoalsPopup: FC<GoalsPopupProps> = (props) => {
  const {
    canvasResponse,
    setCanvasResponse,
    txtMessage,
    setTxtMessage,
    awaitingResponse,
    setMessagesMap,
    onMessage,
    onChangeMessageText,
    messages,
    newChat,
    setAwaitingResponse,
  } = props

  const [isLoading, setIsLoading] = useState(true)
  const { setFlow } = useContext(CaiPopupContext)
  const { sessionId, setSessionId, isSubmitted, setIsSubmitted } =
    useContext(NotificationContext)
  const { data = [], isLoading: isLoadingMessages } =
    useGetChatMessages(sessionId)
  const { t } = useTranslation()
  const [canvasData, setCanvasData] = useState<string | null>(null)
  const [firstDivHeight, setFirstDivHeight] = useState(80)
  const [isDragging, setIsDragging] = useState<boolean>(false)

  const { userInfo } = useContext(AuthContext)
  const orgId = useMemo(() => {
    const getPersonalOrg = userInfo?.userOrganizations?.find(
      (userOrg) =>
        userOrg.organization.category === OrganizationCategory.Personal,
    )
    return (
      getPersonalOrg?.organization.stsOrganisationId ||
      userInfo?.organization?.stsOrganisationId ||
      ''
    )
  }, [userInfo])

  const handleMouseDownFirst = () => {
    setIsDragging(true)
  }

  const handleMouseMove = (e: MouseEvent) => {
    const totalHeight = window.innerHeight
    const minHeightPx = 300 // Minimum height in pixels
    const minHeightPercentage = (minHeightPx / totalHeight) * 100

    if (isDragging) {
      const newHeight = Number(((e.clientY / totalHeight) * 100).toFixed(2))
      if (newHeight >= minHeightPercentage && newHeight <= 80) {
        setFirstDivHeight(newHeight)
      }
    }
  }

  const handleMouseUp = (): void => {
    setIsDragging(false)
  }

  useEffect(() => {
    const handleGetMessages = async () => {
      if (!userInfo?.stsUserId || !userInfo.organization?.stsOrganisationId)
        return
      setIsLoading(true)
      const sessionId = await getOrCreateGoalSession(
        userInfo.stsUserId,
        orgId,
        setFlow,
        setCanvasData,
        setCanvasResponse
      )
      if (!sessionId) return
      setSessionId(sessionId)
      setIsLoading(false)
    }
    handleGetMessages()
  }, [])

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove)
      window.addEventListener('mouseup', handleMouseUp)
    } else {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging])

  useEffect(() => {
    setMessagesMap((state: any) => ({ ...state, goals: data }))
  }, [JSON.stringify(data)])

  useEffect(() => {
    if (canvasData && canvasData.length > 200) {
      setFirstDivHeight(50)
    }
  }, [canvasData])

  if (isLoading || isLoadingMessages) return <CAIIconAnim />

  return (
    <div
      className="d-flex flex-column card card-chat-body border-0  w-100 h-100 gap-2 overflow-hidden"
      style={{
        backgroundColor: 'var(--goals-color)',
        paddingBottom: '0 !important',
      }}
    >
      <div
        className="d-flex position-relative"
        style={{
          overflow: 'hidden',
          height: `${firstDivHeight}%`,
          maxHeight: `${firstDivHeight}%`,
        }}
      >
        <div
          className="resizer"
          id="goalsResizer"
          style={{
            width: '100%',
            bottom: 0,
            left: 0,
            position: 'absolute',
            height: '15px',
            opacity: 0.5, // Adjust opacity to make the icon visible
            cursor: 'col-resize',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'var(--goals-bg-chat-body)',
          }}
          onMouseDown={handleMouseDownFirst}
        >
          <Icon height="24" icon="pepicons-pop:line-x" width="24" />
        </div>
        <div
          className="flex-grow-1 rounded-3 w-100"
          style={{ backgroundColor: 'var(--goals-bg-chat-body) !important' }}
        >
          <div
            className={`d-flex p-3 h-100 align-items-start flex-column ${isDragging ? 'user-select-none' : ''}`}
          >
            <TTSPlayer messages={messages} />
            <MessagesHistory
              awaitingResponse={awaitingResponse}
              id="goalsChatHistory"
              messages={messages}
              choicesElement={<></>}
            />

            <div className="d-flex gap-2 w-100 justify-content-end">
              <Tooltip message="Reset the conversation">
                <button
                  className="btn btn-primary"
                  disabled={awaitingResponse}
                  onClick={async () => {
                    if (!userInfo?.stsUserId || !orgId) return
                    deleteLocalStorageGoalSession()               
                    const newSessionId = await getOrCreateGoalSession(
                      userInfo.stsUserId,
                      orgId,
                      setFlow,
                      setCanvasData,
                      setCanvasResponse
                    )
                    if (!newSessionId) return
                    setSessionId(newSessionId)
                    queryClient.invalidateQueries({
                      queryKey: [QUERY_KEYS.CHAT_GOALS],
                    })
                  }}
                >
                  {t('common.button.reset')}
                </button>
              </Tooltip>

              {!isSubmitted && (
                <SubmitButton
                  setAwaitingResponse={setAwaitingResponse}
                  setCanvasData={setCanvasData}
                  disabled={
                    messages.length <= 1 ||
                    localStorage.getItem('goalSessionStatus') === 'done' ||
                    awaitingResponse
                  }
                />
              )}
            </div>
            <ChatInput
              awaitingResponse={awaitingResponse}
              newChat={newChat}
              setTxtMessage={setTxtMessage}
              txtMessage={txtMessage}
              disabled={
                localStorage.getItem('goalSessionStatus') === 'done' ||
                awaitingResponse
              }
              placeholder={
                localStorage.getItem('goalSessionStatus') === 'done'
                  ? 'resource.pathway.placeholder.doneGoal'
                  : undefined
              }
              onChangeMessageText={onChangeMessageText}
              onMessage={onMessage}
            />
          </div>
        </div>
      </div>

      <div
        style={{
          height: `${100 - firstDivHeight}%`,
          maxHeight: `${100 - firstDivHeight}%`,
          marginBottom: '5px',
          minHeight: '150px',
        }}
      >
        <GoalsPopupCanvas
          canvasData={canvasData}
          canvasResponse={canvasResponse}
        />
      </div>
    </div>
  )
}

export default memo(GoalsPopup)
